// Floating "Ask your team" bubble: the agent workforce from any admin page.
// Team-only by construction: it lives in AppShell (never PortalShell), and it
// renders nothing unless the person can both see agents and start agent work
// and the Agents module is enabled for the workspace. Opening it reuses the
// Agents page's own AskTab + TaskView, preselects the account you're in, and
// sends the page you're on as context. Sends still go through Approvals.
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUpRight, Bot, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useApi } from "@/hooks/useApi";
import { usePerm } from "@/lib/perms";
import { cn } from "@/lib/utils";
import { AskTab } from "@/pages/agents/AskTab";
import { SpendMeter } from "@/pages/agents/SpendMeter";
import type { AgentsResp, AgentsStatus } from "@/pages/agents/shared";
import { showAgentBubble } from "./agentBubbleVisibility";

export function AgentBubble({ moduleEnabled, pageTitle }: { moduleEnabled: boolean; pageTitle: string }) {
  const { can } = usePerm();
  const { pathname, search } = useLocation();
  const visible = showAgentBubble({ canRun: can("agents.run.CREATE"), canRead: can("agents.data.READ"), moduleEnabled, pathname });
  // Remember WHERE it was opened: navigating elsewhere (e.g. following a link in a
  // result) closes the panel without an effect.
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const open = openedAt === pathname;
  const setOpen = (v: boolean) => setOpenedAt(v ? pathname : null);
  const [taskId, setTaskId] = useState<number | null>(null);

  const status = useApi<AgentsStatus>(visible ? "/api/agents/status" : null, { refetchInterval: open ? 15_000 : 60_000 });
  // The full team list is only needed once the panel is open.
  const agents = useApi<AgentsResp>(visible && open ? "/api/agents" : null);

  if (!visible) return null;
  const pending = status.data?.pendingApprovals ?? 0;
  const capReached = !!status.data?.spend?.reached;
  const page = { path: `${pathname}${search}`.slice(0, 300), title: pageTitle };
  const closeOnNav = () => setOpen(false);

  return (
    <>
      {/* Stacked directly above the orange Quick add (+) button, centred on it. */}
      <button type="button" onClick={() => setOpen(true)}
        className={cn(
          "fixed bottom-[8.25rem] right-5 z-40 flex size-12 items-center justify-center rounded-full border border-border bg-card text-primary shadow-lg transition-transform hover:scale-105 active:scale-90 md:bottom-[5.75rem]",
          capReached && "border-destructive/60 text-destructive",
        )}
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
        aria-label={pending ? `Ask your team (${pending} waiting for approval)` : "Ask your team"}
        title="Ask your team">
        <Bot className="size-5" />
        {pending > 0 && (
          <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-cta px-1.5 text-center text-[10px] font-semibold leading-5 text-white" data-testid="agent-bubble-badge">
            {pending > 99 ? "99+" : pending}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="sm:max-w-xl" aria-describedby={undefined}>
          <div className="border-b border-border p-4 pr-12">
            <DialogPrimitive.Title className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
              <Bot className="size-5 text-primary" />Ask your team
            </DialogPrimitive.Title>
            <p className="mt-0.5 text-xs text-muted-foreground">Drafts only. Nothing reaches a client until someone approves it.</p>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            <SpendMeter spend={status.data?.spend} canEditCap={can("settings.integrations.UPDATE")} />
            {pending > 0 && (
              <Link to="/agents?tab=approvals" onClick={closeOnNav}
                className="flex items-center justify-between rounded-md border border-cta/40 bg-cta/10 px-3 py-2 text-xs font-medium hover:bg-cta/15">
                <span className="flex items-center gap-1.5"><ShieldCheck className="size-3.5" />{pending} waiting for approval</span>
                <ArrowUpRight className="size-3.5" />
              </Link>
            )}
            <AskTab compact data={agents.data} canRun={status.data?.canRun ?? true} preselect={null}
              taskId={taskId} onTask={setTaskId} page={page} />
          </div>
          <div className="border-t border-border p-3 text-right">
            <Link to={taskId ? `/agents?task=${taskId}` : "/agents"} onClick={closeOnNav}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              Open the Agents page <ArrowUpRight className="size-3.5" />
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
